import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

/* ── Logo ──────────────────────────────────────────────────── */
const Logo = ({ size = 38 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 140 140" fill="none">
    <defs>
      <linearGradient id="faq-ng" x1="30" y1="15" x2="110" y2="125">
        <stop offset="0%" stopColor="#00FFE0" /><stop offset="50%" stopColor="#00E5CC" /><stop offset="100%" stopColor="#22D97F" />
      </linearGradient>
    </defs>
    <rect width="140" height="140" rx="30" fill="#0F2140" />
    <path d="M70 18L28 37v32c0 26 42 45 42 45s42-19 42-45V37L70 18z" fill="none" stroke="url(#faq-ng)" strokeWidth="4" />
    <circle cx="70" cy="60" r="16" fill="none" stroke="url(#faq-ng)" strokeWidth="2.5" opacity="0.35" />
    <text x="70" y="68" textAnchor="middle" fontFamily="Sora,sans-serif" fontWeight="800" fontSize="20" fill="url(#faq-ng)">J</text>
  </svg>
);

const Ico = {
  arrowLeft: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>,
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>,
  chevDown: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 9l6 6 6-6" /></svg>,
  msgCircle: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>,
  mail: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><path d="M22 6l-10 7L2 6" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" /></svg>,
};

interface FAQItem { question: string; answer: string; category: string; }

export default function FAQPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const categories = ["all", "General", "Products & Tags", "Pricing & Plans", "Technical", "Security"];

  const faqs: FAQItem[] = [
    { category: "General", question: "What is JepTags?", answer: "JepTags is a product authentication and traceability platform that uses NFC, RFID, and QR code technology to help brands protect their products from counterfeiting. Consumers can verify product authenticity with a simple tap or scan — no app required." },
    { category: "General", question: "How does JepTags protect against counterfeiting?", answer: "Each product gets a unique, tamper-proof digital identity through NFC tags, RFID chips, or QR codes. When scanned, the system verifies authenticity in real-time against our secure database, showing the consumer whether the product is genuine or suspicious." },
    { category: "General", question: "Do consumers need to download an app?", answer: "No! Consumers simply tap their smartphone on the NFC tag or scan the QR code. It opens directly in the browser — no app download needed. Works on both iPhone and Android devices." },
    { category: "General", question: "What industries does JepTags serve?", answer: "JepTags serves luxury goods, pharmaceuticals, food & beverage, electronics, cosmetics, fashion, automotive parts, and any industry where product authenticity matters. Our platform is flexible and adapts to any product category." },
    { category: "Products & Tags", question: "What types of tags does JepTags support?", answer: "We support three main technologies: NFC (Near Field Communication) tags for tap-to-verify, RFID tags for supply chain tracking, and QR codes for universal scanning. You can use one or combine multiple technologies for maximum protection." },
    { category: "Products & Tags", question: "How do I attach tags to my products?", answer: "Tags can be embedded in product packaging, attached as labels, sewn into garments, or integrated directly into the product. Our team provides guidance on the best placement for your specific product type." },
    { category: "Products & Tags", question: "Can tags be copied or cloned?", answer: "Our NFC tags use cryptographic authentication that makes them virtually impossible to clone. Each tag has a unique, encrypted signature that is verified against our secure servers. QR codes include additional security layers to prevent duplication." },
    { category: "Products & Tags", question: "What is the lottery/engagement feature?", answer: "JepTags includes a built-in lottery and customer engagement system. When consumers scan your product, they can participate in promotions, win prizes, and engage with your brand — driving repeat interactions and loyalty." },
    { category: "Pricing & Plans", question: "How much does JepTags cost?", answer: "We offer three plans: Starter ($99/month for up to 1,000 tags), Professional ($299/month for up to 25,000 tags), and Enterprise (custom pricing for unlimited tags). All plans include a free trial period with no credit card required." },
    { category: "Pricing & Plans", question: "Is there a free trial?", answer: "Yes! All plans come with a free trial so you can test the platform with your products before committing. No credit card required to start — set up takes less than 5 minutes." },
    { category: "Pricing & Plans", question: "Can I upgrade or downgrade my plan?", answer: "Absolutely. You can change your plan at any time from your business dashboard. Upgrades take effect immediately, and downgrades apply at the next billing cycle." },
    { category: "Technical", question: "Does JepTags integrate with my existing systems?", answer: "Yes. JepTags provides REST APIs for integration with ERP systems, e-commerce platforms, inventory management, and CRM tools. We support Shopify, WooCommerce, SAP, and custom integrations." },
    { category: "Technical", question: "What analytics and reporting are available?", answer: "Our dashboard provides real-time analytics including scan locations, scan frequency, consumer demographics, geographic distribution, suspected counterfeit alerts, and campaign performance metrics." },
    { category: "Technical", question: "Does JepTags work globally?", answer: "Yes. JepTags works in any country with smartphone connectivity. Our platform supports multiple languages and complies with international data protection regulations including GDPR and CCPA." },
    { category: "Security", question: "How is my data protected?", answer: "We use end-to-end encryption, SOC 2 certified infrastructure, and regular security audits. All data is stored in encrypted databases with strict access controls. We never share your data with third parties." },
    { category: "Security", question: "What happens if a counterfeit is detected?", answer: "When a suspicious scan is detected, the system immediately flags it in your dashboard with location data, timestamps, and alert details. You can configure automated notifications and response workflows." },
  ];

  const filtered = faqs.filter(f => {
    const matchSearch = f.question.toLowerCase().includes(searchTerm.toLowerCase()) || f.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = selectedCategory === "all" || f.category === selectedCategory;
    return matchSearch && matchCat;
  });

  return (
    <div className="faq-root">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {/* NAV */}
      <nav className="faq-nav">
        <a href="#" className="faq-nav-logo" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <Logo size={36} /><span className="faq-wm"><span className="jep">Jep</span><span className="tags">tags</span></span>
        </a>
        <button className="faq-back" onClick={() => navigate(-1)}>
          <span className="faq-ico-16">{Ico.arrowLeft}</span> Back
        </button>
      </nav>

      {/* HERO */}
      <section className="faq-hero">
        <div className="faq-badge">Help Center</div>
        <h1>Frequently Asked <span className="grad">Questions</span></h1>
        <p>Everything you need to know about JepTags product authentication and traceability platform.</p>

        {/* Search */}
        <div className="faq-search-wrap">
          <span className="faq-search-ico">{Ico.search}</span>
          <input type="text" placeholder="Search questions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
      </section>

      {/* Category Filter */}
      <div className="faq-cats">
        {categories.map(cat => (
          <button key={cat} className={`faq-cat ${selectedCategory === cat ? 'active' : ''}`} onClick={() => setSelectedCategory(cat)}>
            {cat === "all" ? "All" : cat}
          </button>
        ))}
      </div>

      {/* FAQ List */}
      <section className="faq-list">
        {filtered.length === 0 ? (
          <div className="faq-empty">
            <p>No matching questions found.</p>
            <span>Try a different search term or category.</span>
          </div>
        ) : (
          filtered.map((faq, i) => (
            <div key={i} className={`faq-item ${openIndex === i ? 'open' : ''}`}>
              <button className="faq-q" onClick={() => setOpenIndex(openIndex === i ? null : i)}>
                <div className="faq-q-left">
                  <span className="faq-cat-tag">{faq.category}</span>
                  <h3>{faq.question}</h3>
                </div>
                <span className={`faq-chevron ${openIndex === i ? 'rotated' : ''}`}>{Ico.chevDown}</span>
              </button>
              {openIndex === i && (
                <div className="faq-a"><p>{faq.answer}</p></div>
              )}
            </div>
          ))
        )}
      </section>

      {/* CTA */}
      <section className="faq-cta">
        <div className="faq-cta-box">
          <span className="faq-cta-ico">{Ico.msgCircle}</span>
          <h2>Still have questions?</h2>
          <p>Can't find what you're looking for? Our team is here to help.</p>
          <div className="faq-cta-btns">
            <button className="btn-p" onClick={() => navigate('/contact')}>
              <span className="faq-ico-16">{Ico.mail}</span> Contact Support
            </button>
            <button className="btn-o" onClick={() => navigate('/business/register')}>
              Get Started Free
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
const STYLES = `
.faq-root *,.faq-root *::before,.faq-root *::after{margin:0;padding:0;box-sizing:border-box}
.faq-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;overflow-x:hidden;min-height:100vh;
}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.jep{color:var(--cyan)}.tags{color:var(--teal)}
.faq-ico-16 svg{width:16px;height:16px}

/* NAV */
.faq-nav{position:fixed;top:0;left:0;right:0;z-index:100;padding:16px 40px;display:flex;align-items:center;justify-content:space-between;backdrop-filter:blur(20px);background:rgba(3,10,20,0.85);border-bottom:1px solid rgba(0,229,204,0.06)}
.faq-nav-logo{display:flex;align-items:center;gap:12px;text-decoration:none;cursor:pointer}
.faq-wm{font-size:22px;font-weight:700;letter-spacing:-.5px}
.faq-back{display:flex;align-items:center;gap:6px;background:none;border:1px solid rgba(0,229,204,0.15);color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:13px;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .3s}
.faq-back:hover{border-color:var(--cyan);color:var(--cyan)}

/* BUTTONS */
.btn-p{padding:14px 28px;border:none;border-radius:12px;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep);font-family:'Sora',sans-serif;font-size:15px;font-weight:700;cursor:pointer;transition:transform .3s,box-shadow .3s;display:inline-flex;align-items:center;gap:8px;text-decoration:none}
.btn-p:hover{transform:translateY(-2px);box-shadow:0 12px 40px rgba(0,229,204,0.35)}
.btn-o{padding:14px 28px;border:1.5px solid rgba(0,229,204,0.25);border-radius:12px;background:rgba(0,229,204,0.04);color:var(--cyan);font-family:'Sora',sans-serif;font-size:15px;font-weight:600;cursor:pointer;transition:all .3s;display:inline-flex;align-items:center;gap:8px;text-decoration:none}
.btn-o:hover{border-color:var(--cyan);background:rgba(0,229,204,0.08)}

/* HERO */
.faq-hero{padding:140px 40px 48px;text-align:center;max-width:720px;margin:0 auto}
.faq-badge{display:inline-block;padding:8px 20px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:12px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:24px}
.faq-hero h1{font-size:clamp(32px,5vw,52px);font-weight:800;line-height:1.1;letter-spacing:-2px;margin-bottom:16px}
.faq-hero p{font-size:17px;line-height:1.7;color:rgba(224,240,240,0.5);max-width:560px;margin:0 auto 32px}

/* SEARCH */
.faq-search-wrap{position:relative;max-width:560px;margin:0 auto}
.faq-search-ico{position:absolute;left:18px;top:50%;transform:translateY(-50%);width:20px;height:20px;color:rgba(224,240,240,0.25);display:flex}
.faq-search-ico svg{width:20px;height:20px}
.faq-search-wrap input{width:100%;padding:16px 20px 16px 52px;border-radius:14px;border:1.5px solid rgba(0,229,204,0.1);background:rgba(15,33,64,0.5);color:#e0f0f0;font-family:'Sora',sans-serif;font-size:15px;outline:none;transition:all .3s}
.faq-search-wrap input::placeholder{color:rgba(224,240,240,0.2)}
.faq-search-wrap input:focus{border-color:var(--cyan);box-shadow:0 0 0 3px rgba(0,229,204,0.1)}

/* CATEGORIES */
.faq-cats{display:flex;flex-wrap:wrap;justify-content:center;gap:10px;padding:0 24px;margin-bottom:48px}
.faq-cat{padding:8px 20px;border-radius:100px;font-family:'Sora',sans-serif;font-size:13px;font-weight:600;cursor:pointer;border:1px solid rgba(0,229,204,0.1);background:rgba(15,33,64,0.3);color:rgba(224,240,240,0.5);transition:all .3s}
.faq-cat:hover{border-color:rgba(0,229,204,0.2);color:rgba(224,240,240,0.7)}
.faq-cat.active{background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep);border-color:transparent;font-weight:700}

/* FAQ LIST */
.faq-list{max-width:760px;margin:0 auto;padding:0 24px 60px;display:flex;flex-direction:column;gap:12px}
.faq-empty{text-align:center;padding:60px 20px;color:rgba(224,240,240,0.35)}
.faq-empty p{font-size:16px;margin-bottom:6px}.faq-empty span{font-size:13px}

.faq-item{border-radius:16px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);overflow:hidden;transition:all .3s}
.faq-item:hover,.faq-item.open{border-color:rgba(0,229,204,0.12)}
.faq-q{width:100%;display:flex;align-items:center;justify-content:space-between;padding:20px 24px;background:none;border:none;cursor:pointer;text-align:left;color:#e0f0f0;font-family:'Sora',sans-serif;gap:16px}
.faq-q-left{flex:1}
.faq-cat-tag{display:inline-block;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--cyan);margin-bottom:6px}
.faq-q h3{font-size:16px;font-weight:700;line-height:1.4}
.faq-chevron{width:20px;height:20px;color:rgba(224,240,240,0.3);transition:transform .3s;flex-shrink:0;display:flex}
.faq-chevron svg{width:20px;height:20px}
.faq-chevron.rotated{transform:rotate(180deg);color:var(--cyan)}

.faq-a{padding:0 24px 20px;border-top:1px solid rgba(0,229,204,0.06)}
.faq-a p{font-size:14px;line-height:1.7;color:rgba(224,240,240,0.5);padding-top:16px}

/* CTA */
.faq-cta{padding:20px 40px 100px}
.faq-cta-box{max-width:700px;margin:0 auto;padding:48px;border-radius:28px;background:linear-gradient(135deg,rgba(0,229,204,0.06),rgba(34,217,127,0.04));border:1px solid rgba(0,229,204,0.1);text-align:center}
.faq-cta-ico{width:48px;height:48px;border-radius:14px;background:rgba(0,229,204,0.08);color:var(--cyan);display:flex;align-items:center;justify-content:center;margin:0 auto 16px}
.faq-cta-ico svg{width:28px;height:28px}
.faq-cta-box h2{font-size:24px;font-weight:800;margin-bottom:8px}
.faq-cta-box p{font-size:15px;color:rgba(224,240,240,0.45);margin-bottom:24px}
.faq-cta-btns{display:flex;gap:14px;justify-content:center;flex-wrap:wrap}

/* RESPONSIVE */
@media(max-width:768px){
  .faq-nav{padding:14px 20px}
  .faq-hero{padding:120px 24px 36px}
  .faq-q{padding:16px 18px}.faq-a{padding:0 18px 16px}
}
`;